'use client'

/**
 * EmaSlopeMultiBoard — trend strength across every timeframe the Watcher trades.
 *
 * TWO VIEWS, and the LANE view is the default on purpose. "Which names are trending on the timeframe
 * that governs my lane" is the question a trader actually has; a wall of nine timeframes is the answer
 * to nobody's question. The timeframe grid is still one click away for the full picture.
 *
 * Slope is % PER SESSION, not per bar and not an angle. A drawn angle depends on the chart's y-axis so
 * it cannot be compared between a $900 name and a $3 one, and %/bar cannot be compared BETWEEN
 * timeframes (a 5m bar and a 4h bar are not the same unit of time). Normalising to a session makes both
 * comparisons honest, which is the whole point of ranking.
 *
 * Everything is precomputed server-side, so switching view / lane / EMA re-sorts instantly, no refetch.
 *
 * ── ON SELLING THIS BOARD (owner 2026-08-09: "really tell our story ... convince them we found the edge")
 * The edge claim on this page is a MEASURED one and it is quoted with its base rate, its sample and its
 * caveat. Slope's 2.3x lift is real; overstating it would be the same class of error as the alert copy
 * that promised a push nobody sent. The one thing never to imply here: the study measured REACH (did the
 * name touch +3% within 60 minutes), NOT profit. Reach is opportunity, and this book has a standing
 * finding that the variables predicting opportunity also predict our losses. Say what was measured.
 */

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { TrendingUp, TrendingDown, Minus, Info, Flame } from 'lucide-react'
import type { EmaSlopeMultiBoard as Board, EmaSlopeMultiRow, TfKey, Lane } from '@/lib/dashboard/emaSlopeMulti'

const LANES: { key: Lane; label: string; tf: TfKey; blurb: string }[] = [
  { key: 'scalp', label: 'Scalp', tf: '5m', blurb: 'minutes: is it moving right now' },
  { key: 'day', label: 'Day', tf: '30m', blurb: 'hours: is the session trend intact' },
  { key: 'swing', label: 'Swing', tf: '4h', blurb: 'days: is the multi-day push alive' },
  { key: 'position', label: 'Position', tf: '1D', blurb: 'weeks: is the daily trend up' },
  { key: 'investing', label: 'Investing', tf: '5D', blurb: 'months: is the regime up' },
]
const TFS: TfKey[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1D', '5D']
/** Periods each timeframe can actually seed — mirrors the data layer's ceiling, so the UI never asks
 *  for a 200-EMA on 4h and render a blank the reader would misread as "flat". */
const TF_PERIODS: Record<TfKey, number[]> = {
  '1m': [9, 50, 200], '5m': [9, 50, 200], '15m': [9, 50, 200], '30m': [9, 50, 200],
  '1h': [9, 50], '4h': [9, 20], '1D': [9, 50, 200], '5D': [9, 50, 200],
}
/**
 * WHY A LANE OFFERS THE PERIODS IT DOES (owner 2026-08-09: "swing had different ema, it had 9 and 20").
 * It is not arbitrary and it is not a bug: intraday history is a ~9-day rolling window, so a 4-hour
 * chart has roughly 40 bars in existence and a 50-EMA cannot be seeded from them. Showing a 50 anyway
 * would print a confident number built on a truncated seed. Saying so out loud is better than a reader
 * quietly concluding the board is inconsistent.
 */
const PERIOD_NOTE: Partial<Record<TfKey, string>> = {
  '4h': 'The 4-hour chart offers a 9 and a 20, not a 50: intraday history is a ~9-day window, which is only about 40 four-hour bars. A 50 would need a seed that does not exist.',
  '1h': 'The 1-hour chart offers a 9 and a 50, not a 200: 200 hourly bars is ~12.5 sessions, past the ~9-day intraday window.',
}

const fmtPx = (n: number) => (n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : n.toFixed(2))
const signed = (n: number, d = 2) => `${n >= 0 ? '+' : ''}${n.toFixed(d)}`
const fmtDv = (n: number) => (n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(0)}M` : `$${Math.round(n / 1e3)}K`)

/** Colour by MAGNITUDE, not just sign — a +0.05%/session drift should not look like a +8% rip. */
function toneFor(v: number | null | undefined): string {
  if (v == null) return 'text-zinc-600'
  const a = Math.abs(v)
  if (a < 0.15) return 'text-zinc-500'
  if (v > 0) return a >= 3 ? 'text-emerald-300 font-semibold' : a >= 1 ? 'text-emerald-400' : 'text-emerald-500/70'
  return a >= 3 ? 'text-rose-300 font-semibold' : a >= 1 ? 'text-rose-400' : 'text-rose-500/70'
}

function Cell({ v }: { v: number | null | undefined }) {
  if (v == null) return <span className="text-zinc-700" title="not enough history on this timeframe to seed this EMA">–</span>
  return <span className={toneFor(v)}>{signed(v)}</span>
}

/**
 * Company mark, with a monogram fallback so a missing logo is never a broken image icon.
 *
 * 🚨 SOURCED FROM THE SHARED PROXY `/api/logo?symbol=`, NEVER FROM stocks.logo_url DIRECTLY. That
 * column holds cdn.polygon.io URLs and that host is dead: 148 of 148 images on this board failed to
 * load when it was wired straight to the column. The proxy (app/api/logo/route.ts) already solves
 * this for the rest of the site, with Redis caching, rate limiting, redirect following and fallback
 * sources, which is why MoneyTreemap has used it via its own logoSrc() helper all along.
 * ⚠ The `logo` prop is now only a HINT that the row has something on file. The BYTES always come from
 * the proxy. Do not "simplify" this back to src={logo}; that is the bug this comment exists to stop.
 */
function Logo({ symbol, logo, size = 26 }: { symbol: string; logo: string | null; size?: number }) {
  const [bad, setBad] = useState(false)
  if (!logo || bad) {
    return (
      <span
        className="grid shrink-0 place-items-center rounded-md bg-zinc-800 text-[10px] font-bold text-zinc-400 ring-1 ring-zinc-700/60"
        style={{ width: size, height: size }}
        aria-hidden
      >
        {symbol.slice(0, 2)}
      </span>
    )
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={`/api/logo?symbol=${encodeURIComponent(symbol)}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setBad(true)}
      className="shrink-0 rounded-md bg-white/95 object-contain ring-1 ring-zinc-700/60"
      style={{ width: size, height: size }}
    />
  )
}

/**
 * Sparkline over the same lookback the slope is measured on.
 *
 * ⚠ NO Y-AXIS ZERO AND THAT IS DELIBERATE: it is normalised to its own min/max so the SHAPE of the move
 * is legible at 28px tall. It shows the path, not the magnitude — magnitude is the number beside it,
 * which is why the two are always rendered together and never on their own.
 */
function Spark({ data, up, w = 96, h = 28 }: { data?: number[]; up: boolean; w?: number; h?: number }) {
  if (!data || data.length < 2) return <span className="inline-block text-[10px] text-zinc-700" style={{ width: w }}>–</span>
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min || 1
  const pad = 2
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (w - pad * 2) + pad
    const y = h - pad - ((v - min) / span) * (h - pad * 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  const stroke = up ? '#34d399' : '#fb7185'
  const fill = up ? 'rgba(52,211,153,0.13)' : 'rgba(251,113,133,0.13)'
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden>
      <polygon points={`${pad},${h - pad} ${pts.join(' ')} ${w - pad},${h - pad}`} fill={fill} />
      <polyline points={pts.join(' ')} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={(w - pad).toFixed(1)} cy={pts[pts.length - 1].split(',')[1]} r="1.9" fill={stroke} />
    </svg>
  )
}

/** Magnitude bar. Scaled to the strongest name ON SCREEN so the top of the board is always full-width
 *  and the reader can see relative steepness without reading every number. */
function SlopeBar({ v, max }: { v: number | null; max: number }) {
  if (v == null || max <= 0) return null
  const pct = Math.min(100, (Math.abs(v) / max) * 100)
  return (
    <div className="mt-1 h-[3px] w-full overflow-hidden rounded-full bg-zinc-800">
      <div className={`h-full rounded-full ${v >= 0 ? 'bg-emerald-500/70' : 'bg-rose-500/70'}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function EmaSlopeMultiBoardClient({ initial }: { initial: Board }) {
  const [view, setView] = useState<'lane' | 'grid'>('lane')
  const [lane, setLane] = useState<Lane>('swing')
  const [period, setPeriod] = useState<number>(9)
  const [q, setQ] = useState('')

  const activeTf = LANES.find((l) => l.key === lane)!.tf
  const periodsHere = TF_PERIODS[activeTf]

  const rows = useMemo(() => {
    const needle = q.trim().toUpperCase()
    const list = needle ? initial.rows.filter((r) => r.symbol.includes(needle) || (r.name ?? '').toUpperCase().includes(needle)) : initial.rows
    const key = (r: EmaSlopeMultiRow) => r.tf[activeTf]?.perSession[period] ?? null
    return [...list].sort((a, b) => (key(b) ?? -1e9) - (key(a) ?? -1e9))
  }, [initial.rows, activeTf, period, q])

  /** Bar scale: the steepest absolute slope currently listed. */
  const maxAbs = useMemo(() => {
    let m = 0
    for (const r of rows) {
      const v = r.tf[activeTf]?.perSession[period]
      if (v != null) m = Math.max(m, Math.abs(v))
    }
    return m
  }, [rows, activeTf, period])

  const leaders = rows.slice(0, 3)
  const laneMeta = LANES.find((l) => l.key === lane)!

  return (
    <div className="space-y-4">
      {/* ── controls ─────────────────────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-zinc-800 bg-zinc-900/50 p-0.5">
          {(['lane', 'grid'] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`rounded-lg px-3 py-1.5 text-[12px] font-medium transition ${view === v ? 'bg-zinc-700/70 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}>
              {v === 'lane' ? 'By lane' : 'All timeframes'}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter symbol…"
          className="ml-auto w-36 rounded-xl border border-zinc-800 bg-zinc-900/50 px-3 py-1.5 text-[12px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600" />
      </div>

      {view === 'lane' && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {LANES.map((l) => (
              <button key={l.key} onClick={() => { setLane(l.key); if (!TF_PERIODS[l.tf].includes(period)) setPeriod(9) }}
                className={`rounded-xl border px-3 py-2 text-left transition ${lane === l.key ? 'border-emerald-500/40 bg-emerald-500/[0.07] shadow-[0_0_0_1px_rgba(16,185,129,0.10)]' : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-700'}`}>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-[13px] font-semibold ${lane === l.key ? 'text-emerald-200' : 'text-zinc-300'}`}>{l.label}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${lane === l.key ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-800 text-zinc-400'}`}>{l.tf}</span>
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-500">{l.blurb}</div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-zinc-500">EMA</span>
            {periodsHere.map((p) => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`rounded-lg px-2.5 py-1 text-[12px] transition ${period === p ? 'bg-zinc-700/70 text-zinc-100' : 'text-zinc-400 hover:text-zinc-200'}`}>{p}</button>
            ))}
            <span className="ml-1 text-[11px] text-zinc-600">on the {activeTf} chart</span>
          </div>
          {PERIOD_NOTE[activeTf] && (
            <p className="-mt-1 flex items-start gap-1.5 text-[11px] leading-relaxed text-zinc-500">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{PERIOD_NOTE[activeTf]}</span>
            </p>
          )}

          {/* ── LEADERS: the three steepest, shown as cards so the board opens with an answer ────── */}
          {leaders.length > 0 && maxAbs > 0 && (
            <div className="grid gap-2 sm:grid-cols-3">
              {leaders.map((r, i) => {
                const s = r.tf[activeTf]
                const v = s?.perSession[period] ?? null
                return (
                  <Link key={r.symbol} href={`/stocks/${r.symbol}`}
                    className="group rounded-2xl border border-zinc-800/70 bg-gradient-to-br from-zinc-900/70 to-zinc-950 p-3 transition hover:border-emerald-500/30">
                    <div className="flex items-center gap-2">
                      <Logo symbol={r.symbol} logo={r.logo} size={30} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[14px] font-bold text-zinc-100 group-hover:text-emerald-300">{r.symbol}</span>
                          {i === 0 && <Flame className="h-3.5 w-3.5 text-amber-400" />}
                        </div>
                        {r.name && <div className="truncate text-[11px] text-zinc-500">{r.name}</div>}
                      </div>
                      <div className="ml-auto text-right">
                        <div className={`text-[15px] font-bold tabular-nums ${toneFor(v)}`}>{v == null ? '–' : signed(v)}</div>
                        <div className="text-[10px] text-zinc-600">%/session</div>
                      </div>
                    </div>
                    <div className="mt-2 flex items-end justify-between gap-2">
                      <Spark data={r.spark[activeTf]} up={(v ?? 0) >= 0} w={130} h={34} />
                      <div className="text-right text-[11px] leading-tight text-zinc-500">
                        <div className="tabular-nums text-zinc-300">${fmtPx(r.price)}</div>
                        <div className="tabular-nums">{fmtDv(r.dollarVol)}</div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* ── table ────────────────────────────────────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-2xl border border-zinc-800/70 bg-zinc-900/30">
        <table className="w-full min-w-[760px] text-[13px]">
          <thead>
            <tr className="border-b border-zinc-800/70 text-[11px] uppercase tracking-wide text-zinc-500">
              <th className="px-3 py-2.5 text-left font-medium">#</th>
              <th className="px-3 py-2.5 text-left font-medium">Symbol</th>
              {view === 'lane' && <th className="px-3 py-2.5 text-left font-medium">{activeTf} shape</th>}
              <th className="px-3 py-2.5 text-right font-medium">Price</th>
              {view === 'lane'
                ? <>
                    <th className="px-3 py-2.5 text-right font-medium">{activeTf} slope</th>
                    <th className="px-3 py-2.5 text-right font-medium">Move</th>
                    <th className="px-3 py-2.5 text-center font-medium">Stack</th>
                    <th className="px-3 py-2.5 text-right font-medium">$ Vol</th>
                  </>
                : TFS.map((t) => <th key={t} className="px-2.5 py-2.5 text-right font-medium">{t}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const s = r.tf[activeTf]
              const v = s?.perSession[period] ?? null
              return (
                <tr key={r.symbol} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-800/20">
                  <td className="px-3 py-2 tabular-nums text-zinc-600">{i + 1}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Logo symbol={r.symbol} logo={r.logo} />
                      <div className="min-w-0">
                        <Link href={`/stocks/${r.symbol}`} className="font-semibold text-zinc-100 hover:text-emerald-300">{r.symbol}</Link>
                        {r.name && <div className="max-w-[190px] truncate text-[11px] text-zinc-500">{r.name}</div>}
                      </div>
                    </div>
                  </td>
                  {view === 'lane' && (
                    <td className="px-3 py-1.5"><Spark data={r.spark[activeTf]} up={(v ?? 0) >= 0} /></td>
                  )}
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtPx(r.price)}</td>
                  {view === 'lane' ? (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div><Cell v={v} /><span className="ml-0.5 text-[10px] text-zinc-600">%/sess</span></div>
                        <SlopeBar v={v} max={maxAbs} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{s?.total[period] != null ? signed(s.total[period]!, 1) + '%' : '–'}</td>
                      <td className="px-3 py-2 text-center">
                        {s?.stacked == null ? <Minus className="mx-auto h-3.5 w-3.5 text-zinc-700" />
                          : s.stacked ? <TrendingUp className="mx-auto h-3.5 w-3.5 text-emerald-400" />
                          : <TrendingDown className="mx-auto h-3.5 w-3.5 text-rose-400" />}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{fmtDv(r.dollarVol)}</td>
                    </>
                  ) : TFS.map((t) => (
                    <td key={t} className="px-2.5 py-2 text-right tabular-nums"><Cell v={r.tf[t]?.perSession[9] ?? null} /></td>
                  ))}
                </tr>
              )
            })}
            {!rows.length && (
              <tr><td colSpan={view === 'lane' ? 8 : 3 + TFS.length} className="px-3 py-10 text-center text-[13px] text-zinc-500">No names match that filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-zinc-500">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Slope is <strong className="text-zinc-400">percent per session</strong> so timeframes are comparable to each
          other. The <strong className="text-zinc-400">shape</strong> column is the same lookback the slope is measured
          over, scaled to its own high and low. It shows the path, not the size, which is what the number beside it is
          for. In the grid view every column is the <strong className="text-zinc-400">9-EMA</strong>. A dash means that
          timeframe cannot seed that EMA from available history. {initial.rows.length} names, {initial.universe} screened.
        </span>
      </p>
    </div>
  )
}
